import { Popover, Tooltip } from 'flowbite-react';
import Button from 'components/button/button';
import Table, { TableColDef } from 'components/table/Table';
import moment, { Moment } from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HiOutlineAdjustments, HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineCalendar } from 'react-icons/hi';
import { ActivityLogParams, downloadActivityLogsFile } from 'src/api/activityLogsApi';
import InputField from 'src/components/admin/form/inputField';
import Calendar from 'src/components/HomeFilter/Calendar';
import SearchInput from 'src/components/input/SearchInput';
import { DATE_FORMAT, defaultPageCount } from 'src/utils/constants';
import { queryFilter } from 'src/utils/proptypes';
import { useGetActivityLogs } from 'src/hooks/useActivityLogs';
import { useLoading } from 'src/hooks/useLoading';
import { ActivityLog } from 'src/types/activityLog.type';
import { twMerge } from 'tailwind-merge';
import ViewActivityLogModal from './ViewActivityLogModal';
import FilterModal from './FilterModal';
import SiteFilterSelect from 'src/components/admin/site/SiteFilterSelect';
import { applySiteFilter } from 'src/utils/siteFilter.util';
import { useAdminSites } from 'src/hooks/useAdminSites';
import OrgNameCell from 'src/components/admin/OrgNameCell';
import { formatOrgWithParent } from 'src/utils/organization.util';

const ActivityLogsPage = () => {
   const [t] = useTranslation();
   const { loading, setLoading } = useLoading();

   const [filters, setFilters] = useState<queryFilter & ActivityLogParams>({
      page: 0,
      limit: defaultPageCount,
   });
   const { siteName } = useAdminSites();

   const [openCalendar, setOpenCalendar] = useState(false);
   const [selectedMonth, setSelectedMonth] = useState(moment());
   const [dateFilter, setDateFilter] = useState<moment.MomentInput[]>([]);
   const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
   const [openViewModal, setOpenViewModal] = useState(false);
   const [selectedLog, setSelectedLog] = useState<ActivityLog | undefined>();
   const [openMobileFilterModal, setOpenMobileFilterModal] = useState(false);

   useEffect(() => {
      const handleResize = () => {
         if (window.innerWidth >= 768) setOpenMobileFilterModal(false);
      };
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
   }, []);

   // Fetch get activityLogs
   const { data: getActivityLogsRes, isLoading, isFetching, refetch } = useGetActivityLogs(filters);

   useEffect(() => {
      setLoading(isLoading || isFetching);
   }, [isLoading, isFetching, setLoading]);

   useEffect(() => {
      refetch();
   }, [filters]);

   // Search
   const handleSearch = (value: string) => {
      setFilters(prev => ({ ...prev, page: 0, searchQuery: value }));
   };

   // Sort
   const handleSort = (model: { field: keyof ActivityLog; order: 'ASC' | 'DESC' | 'NONE' }) => {
      if (model && model.order !== 'NONE') {
         setFilters(prev => ({
            ...prev,
            sortBy: model.field,
            sortByType: model.order,
         }));
      } else {
         setFilters(prev => {
            const { sortBy: _sortBy, sortByType: _sortByType, ...rest } = prev;
            return rest;
         });
      }
   };

   // Export
   const handleExport = async () => {
      setLoading(true);
      await downloadActivityLogsFile(filters);
      setLoading(false);
   };

   const activityLogs = useMemo<ActivityLog[]>(
      () =>
         getActivityLogsRes?.data?.entities?.map((log: ActivityLog) => {
            return log;
         }),
      [getActivityLogsRes?.data],
   );

   const metadata = useMemo(
      () =>
         getActivityLogsRes?.data?.metadata || {
            total: 0,
            totalPages: defaultPageCount,
         },
      [getActivityLogsRes?.data],
   );

   const columns: TableColDef<ActivityLog>[] = useMemo(
      () => [
         {
            headerName: 'log.id',
            field: 'id',
            sortable: true,
            minWidth: 180,
            flex: 1,
            render: (row: ActivityLog) => {
               return (
                  <Tooltip content={String(row.logId ?? '')} trigger="hover" style="dark">
                     <p className="self-center mb-0 text-black font-semibold text-sm line-clamp-2">{row.logId}</p>
                  </Tooltip>
               );
            },
         },
         {
            headerName: 'log.created',
            field: 'created',
            minWidth: 150,
            render: (row: ActivityLog) => {
               return (
                  <Tooltip content={String(row.created ?? 'Never')} trigger="hover" style="dark">
                     <div className="self-center text-black text-sm whitespace-nowrap w-fit line-clamp-2">{row.created || 'Never'}</div>
                  </Tooltip>
               );
            },
         },
         {
            headerName: t('admin.sites.column'),
            field: 'siteId',
            minWidth: 120,
            render: (row: ActivityLog) => {
               return <p className="self-center mb-0 text-black text-sm w-fit line-clamp-2">{siteName(row.siteId)}</p>;
            },
         },
         {
            headerName: 'log.organizationId',
            field: 'organizationId',
            minWidth: 180,
            flex: 1,
            render: (row: ActivityLog) => {
               return (
                  <Tooltip content={String(row.organizationId ?? '')} trigger="hover" style="dark">
                     <p className="self-center mb-0 text-black text-sm line-clamp-2">{row.organizationId}</p>
                  </Tooltip>
               );
            },
         },
         {
            headerName: 'log_org.name',
            field: 'organizationName',
            minWidth: 160,
            sortable: true,
            render: (row: ActivityLog) => {
               return (
                  <Tooltip content={formatOrgWithParent(t, row.organizationName, row.parentOrganizationName)} trigger="hover" style="dark">
                     <OrgNameCell className="self-center" name={row.organizationName} parentName={row.parentOrganizationName} nameClassName="text-black text-sm line-clamp-2" />
                  </Tooltip>
               );
            },
         },
         {
            headerName: 'log_org.slug',
            field: 'organizationSlug',
            minWidth: 160,
            sortable: true,
            render: (row: ActivityLog) => {
               return (
                  <Tooltip content={String(row.organizationSlug ?? '')} trigger="hover" style="dark">
                     <p className="self-center mb-0 text-black text-sm w-fit line-clamp-2">{row.organizationSlug}</p>
                  </Tooltip>
               );
            },
         },
         {
            headerName: 'requestType',
            field: 'requestType',
            minWidth: 130,
            sortable: true,
            render: (row: ActivityLog) => {
               return <p className="self-center mb-0 text-black text-sm line-clamp-2 break-words">{row.requestType}</p>;
            },
         },
         {
            headerName: 'requestBody',
            field: 'requestBody',
            minWidth: Object.values(expandedRows)?.some((b: boolean) => b) ? 300 : 120,
            sortable: true,
            render: (row: ActivityLog) => {
               const toggleExpand = (logId: string) => {
                  setExpandedRows(prev => ({
                     ...prev,
                     [logId]: !prev[logId],
                  }));
               };

               const expanded = expandedRows[row.logId] ?? false;

               return (
                  <p
                     className={twMerge(
                        'self-center mb-0 text-black text-sm w-fit cursor-pointer',
                        !expanded && 'hover:scale-110',
                        expanded && 'max-w-[400px] line-clamp-5',
                     )}
                     onClick={e => {
                        e.stopPropagation();
                        e.preventDefault();

                        toggleExpand(row.logId);
                     }}
                  >
                     {expanded ? JSON.stringify(row.requestBody, null, 2) : '{...}'}
                  </p>
               );
            },
         },
         {
            headerName: 'userId',
            field: 'userId',
            minWidth: 180,
            sortable: true,
            render: (row: ActivityLog) => {
               return <p className="self-center mb-0 text-black text-sm w-fit line-clamp-2">{row.userId}</p>;
            },
         },
         {
            headerName: 'user.emailAddress',
            field: 'emailAddress',
            minWidth: 180,
            sortable: true,
            render: (row: ActivityLog) => {
               return <p className="self-center mb-0 text-black text-sm w-fit line-clamp-2">{row.email}</p>;
            },
         },
         {
            headerName: 'user.firstName',
            field: 'firstName',
            minWidth: 100,
            sortable: true,
            render: (row: ActivityLog) => {
               return <p className="self-center mb-0 text-black text-sm w-fit line-clamp-2">{row.firstName}</p>;
            },
         },
         {
            headerName: 'user.lastName',
            field: 'lastName',
            minWidth: 100,
            sortable: true,
            render: (row: ActivityLog) => {
               return <p className="self-center mb-0 text-black text-sm w-fit line-clamp-2">{row.lastName}</p>;
            },
         },
      ],
      [filters.type, expandedRows, siteName, t],
   );

   return (
      <div className="flex flex-col bg-white h-full gap-5">
         {/* Top actions — mobile-first responsive layout */}
         <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-6 lg:gap-8">
            {/* Left group: Search + Filter controls */}
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4 min-w-0 flex-1">
               {/* Search input — full width on mobile, constrained on md+ */}
               <div className="flex flex-row items-center gap-2 w-full md:w-auto md:min-w-[280px]">
                  <div className="flex-1">
                     <SearchInput handleSearch={handleSearch} searchValue={filters?.searchQuery || ''} />
                  </div>
                  <div className="md:hidden shrink-0">
                     <Tooltip content={<p className="font-medium text-sm mb-0">Filter</p>}>
                        <div
                           className="w-8.5 aspect-square border-borderStrong border rounded-6 flex items-center justify-center cursor-pointer hover:scale-105"
                           onClick={() => setOpenMobileFilterModal(!openMobileFilterModal)}
                        >
                           <HiOutlineAdjustments className="w-4 h-4" />
                        </div>
                     </Tooltip>
                  </div>
               </div>

               {/* Date range picker */}
               <div className="hidden md:flex w-full md:w-auto md:min-w-[230px]">
                  <Popover
                     open={openCalendar}
                     onOpenChange={active => {
                        if (!active) {
                           setSelectedMonth(dateFilter.length !== 0 ? moment(dateFilter[0]) : moment());
                        }

                        setOpenCalendar(active);
                     }}
                     content={
                        <div>
                           <div className="flex justify-between">
                              <button
                                 className="active:bg-border hover:bg-border min-w-6 min-h-6 flex items-center justify-center rounded"
                                 onClick={e => {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    setSelectedMonth(preValue => preValue.clone().subtract(1, 'month'));
                                 }}
                              >
                                 <HiOutlineChevronLeft size={18} color="black" />
                              </button>
                              <span className="text-black font-bold text-sm">{selectedMonth.format('MMMM YYYY')}</span>
                              <button
                                 className="active:bg-border hover:bg-border min-w-6 min-h-6 flex items-center justify-center rounded disabled:opacity-50 disabled:bg-border"
                                 disabled={selectedMonth.isAfter(moment().add(10, 'year'))}
                                 onClick={e => {
                                    e.stopPropagation();
                                    e.preventDefault();

                                    setSelectedMonth(preValue => preValue.clone().add(1, 'month'));
                                 }}
                              >
                                 <HiOutlineChevronRight size={18} color="black" />
                              </button>
                           </div>
                           <div className="mt-3">
                              <Calendar
                                 disabledBefore={moment().subtract(10, 'year')}
                                 range={dateFilter}
                                 selectedMonth={selectedMonth}
                                 onChange={(value?: Moment) => {
                                    if (!value) return;

                                    if (dateFilter.length % 2 === 0) {
                                       setDateFilter([value]);
                                       return;
                                    }

                                    const [firstDate] = dateFilter;
                                    const fromDate = moment.min([firstDate as Moment, value]);
                                    const toDate = moment.max([firstDate as Moment, value]);

                                    setDateFilter([fromDate, toDate]);
                                    setFilters(p => ({
                                       ...p,
                                       fromDate: fromDate.format(DATE_FORMAT.SERVER_DATE),
                                       toDate: toDate.format(DATE_FORMAT.SERVER_DATE),
                                    }));
                                    setOpenCalendar(false);
                                 }}
                                 isOnAdminSite
                              />
                           </div>
                        </div>
                     }
                     theme={{
                        base: 'w-[calc(100vw-30px)] !left-1.75 p-3.75 sm:max-w-[346px]',
                        content: 'z-10 overflow-hidden rounded-none',
                     }}
                  >
                     <InputField
                        acceptChars={/[0-9/]/}
                        clearable
                        placeholder={t('activityLogs.dateRange')}
                        endIcon={<HiOutlineCalendar size={16} color="gray" />}
                        onEndIconClick={() => setOpenCalendar(!openCalendar)}
                        theme={{ border: 'border-gray-200 max-h-8.5', input: 'min-h-8', endIcon: 'w-6.5 min-w-6.5 h-6.5 min-h-6.5' }}
                        value={
                           dateFilter?.length === 2
                              ? `${moment(dateFilter?.[0]).format(DATE_FORMAT.STANDARD)} - ${moment(dateFilter?.[1]).format(DATE_FORMAT.STANDARD)}`
                              : ''
                        }
                        onClear={() => {
                           setDateFilter([]);
                           setFilters(preValues => {
                              return {
                                 ...preValues,
                                 fromDate: undefined,
                                 toDate: undefined,
                              };
                           });
                        }}
                     />
                  </Popover>
               </div>

               {/* Site filter — every site unless one is picked (NPT-2096) */}
               <SiteFilterSelect
                  className="hidden md:flex w-full md:w-auto md:min-w-[170px]"
                  value={filters.filterSiteId}
                  onChange={filterSiteId => setFilters(prev => applySiteFilter(prev, filterSiteId))}
               />
            </div>

            {/* Right group: Action buttons — stack on mobile, row on md+ */}
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-end md:gap-3 shrink-0">
               <Button
                  className="h-8.5 text-sm md:w-auto bg-inherit text-primary hover:bg-secondary hover:text-white hover:border-none"
                  onClick={handleExport}
                  disabled={loading}
               >
                  {t('activityLogs.export')}
               </Button>
            </div>
         </div>

         <div className="flex flex-col flex-1 mt-3 justify-between h-full overflow-hidden">
            <Table ariaLabel={t('admin.tableLabels.activityLogs')}
               theme={{
                  head: {
                     base: 'bg-white border-b-2 group/head',
                     cell: {
                        base: 'px-4 py-1.5 bg-white text-black text-xs normal-case text-left',
                     },
                  },
                  row: {
                     base: 'cursor-pointer py-1.5',
                     hovered: 'hover:bg-gray-50',
                     striped: '',
                  },
               }}
               isLoading={isFetching}
               columns={columns}
               rows={activityLogs}
               currentPage={filters?.page || 0}
               totalPage={metadata.totalPages}
               onSort={models => {
                  const model = models[0];
                  if (!model) return;

                  handleSort({
                     field: model.field as keyof ActivityLog,
                     order: model.order.toUpperCase() as 'ASC' | 'DESC' | 'NONE',
                  });
               }}
               onPageChanged={(page: number) => {
                  setFilters(preValue => ({
                     ...preValue,
                     page,
                  }));
               }}
               onRowSelect={row => {
                  setSelectedLog(row);
                  setOpenViewModal(!openViewModal);
               }}
            />

            {/* ViewActivityLogModal */}
            <ViewActivityLogModal show={openViewModal} onClose={() => setOpenViewModal(!openViewModal)} log={selectedLog} />
         </div>

         <FilterModal
            isOpen={openMobileFilterModal}
            setIsOpen={setOpenMobileFilterModal}
            filters={filters}
            setFilters={setFilters}
            openCalendar={openCalendar}
            setOpenCalendar={setOpenCalendar}
            selectedMonth={selectedMonth}
            setSelectedMonth={setSelectedMonth}
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
         />
      </div>
   );
};

export default ActivityLogsPage;
